import type { ReactNode } from 'react';
import { CheckCircle, Info, Warning, WarningCircle, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

export type BannerTone = 'error' | 'warning' | 'info' | 'success';

export interface BannerProps {
  tone?: BannerTone;
  title?: string;
  children?: ReactNode;
  /** Optional trailing action, e.g. a Retry button. */
  action?: ReactNode;
  className?: string;
}

const TONE: Record<BannerTone, { box: string; icon: PhosphorIcon }> = {
  error: { box: 'bg-feedback-error-bg text-feedback-error', icon: WarningCircle },
  warning: { box: 'bg-feedback-warning-bg text-feedback-warning', icon: Warning },
  info: { box: 'bg-feedback-info-bg text-feedback-info', icon: Info },
  success: { box: 'bg-feedback-success-bg text-feedback-success-text', icon: CheckCircle },
};

/** Inline message block: validation/server failures, locked-out notices, "connect Instagram" prompts. */
export function Banner({ tone = 'info', title, children, action, className }: BannerProps) {
  const { box, icon } = TONE[tone];
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cx('flex items-start gap-(--mbm-space-3) rounded-(--mbm-radius-md) p-(--mbm-space-4)', box, className)}
    >
      <span className="mt-px shrink-0">
        <Icon icon={icon} size="lg" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-(--mbm-space-0-5)">
        {title ? <p className="text-label-large">{title}</p> : null}
        {children ? <div className="text-body-small">{children}</div> : null}
      </div>
      {action}
    </div>
  );
}
